/**
 * Guide engine v3 - catalog + progress client (#1026, epic #1020). Spec R7.
 *
 * Wraps the user-scoped guide endpoints. Routes are global (not group-scoped);
 * the API base already ends in `/api`, so paths omit it. Progress is
 * COMPLETED-ONLY in v3 (R7): the client never writes dismissed / versionSeen /
 * lastStepId, and effectivePriority derives from `completed` alone. Clean-room,
 * no v1 reuse.
 */

import { apiGlobal } from '../../services/api';

/**
 * GET /api/guides - env-gated catalog joined with the caller's progress.
 * @returns {Promise<object[]>} catalog entries (completed-only progress).
 */
export const fetchCatalog = async () => {
  const data = await apiGlobal.get('/guides');
  return Array.isArray(data && data.guides) ? data.guides : [];
};

/**
 * PUT /api/guides/{moduleId}/progress - record completion (R7). The only progress
 * write v3 makes; reaching `_exit` triggers it.
 * @param {string} moduleId
 * @param {string} [completedAt] ISO timestamp; defaults to now.
 */
export const writeCompleted = (moduleId, completedAt = new Date().toISOString()) =>
  apiGlobal.put(`/guides/${encodeURIComponent(moduleId)}/progress`, { completed: completedAt });

/**
 * Per-user effective priority mirror of the backend derivation (completed -> done),
 * so a local completion updates launcher badges without a catalog refetch.
 * @param {string} priority
 * @param {{completed?: string}|null} progress
 * @returns {string}
 */
export const computeEffectivePriority = (priority, progress) =>
  (progress && progress.completed ? 'done' : priority);

/**
 * POST /api/guides/sandbox/reset - empty the caller's guide practice space
 * (#1021). Scope is fixed server-side to the caller's own guideSandbox
 * group(s); the group, memberships, and completed progress stay.
 * @returns {Promise<{sandboxGroups: number, deleted: object}>}
 */
export const resetSandbox = () => apiGlobal.post('/guides/sandbox/reset');
